import React, { useEffect, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import {
    CardSection,
    HeaderActionButton,
    UploaderProvider,
} from '@europrocurement/flexy-components';
import { type DossierOpenSearch, useFormaliteService } from '@europrocurement/l2d-domain';
import {
    DocumentExtension,
    type DocumentGed,
} from '@europrocurement/l2d-modules/modules/Dossiers/hooks';
import { useSnackbar } from 'notistack';
import { arrowLeftIcon, plusIcon } from '@europrocurement/l2d-icons';
import { getFileExtension } from '@europrocurement/l2d-utils';
import { useDownload } from '@europrocurement/l2d-modules/hooks';
import { useDocumentPreviewContext } from '@europrocurement/l2d-modules/modules/Dossiers/providers';
import AttachmentDocumentsHeading from '../AttachmentDocumentsHeading/AttachmentDocumentsHeading';
import DocumentItem from '../../../DocumentItem/DocumentItem';
import DocumentViewer from '../../../DocumentViewer/DocumentViewer';
import DossierDocumentGedCreateForm from '../../../DossierDocumentGedCreateForm/DossierDocumentGedCreateForm';

export type DocumentsModalContentProps = {
    reglementId?: number;
    reglementsDocGed: DocumentGed[];
    dossier: DossierOpenSearch;
    setReglementsDocs: React.Dispatch<React.SetStateAction<DocumentGed[]>>;
};

const DocumentsModalContent: React.FunctionComponent<DocumentsModalContentProps> = function ({
    reglementId,
    reglementsDocGed,
    dossier,
    setReglementsDocs,
}) {
    const { palette } = useTheme();
    const { documentGedApi } = useFormaliteService();
    const { enqueueSnackbar } = useSnackbar();
    const { download } = useDownload();

    const { setPreviewLoading, setDocumentPreview } = useDocumentPreviewContext();
    const [filteredDocGed, setFilteredDocGed] = useState<DocumentGed[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isDisplayedForm, setIsDisplayedForm] = useState<boolean>(false);

    useEffect(() => {
        if (reglementsDocGed.length > 0) {
            setFilteredDocGed(
                reglementsDocGed.filter((reglementDoc) => reglementDoc.idReglement === reglementId),
            );
        }
    }, [reglementId, reglementsDocGed]);

    const handleShow = async (e: React.MouseEvent, reglementDoc: DocumentGed) => {
        if (reglementDoc.idDocument) {
            setPreviewLoading(true);
            setIsSubmitting(true);
            try {
                const docGedContent = await documentGedApi.apiDocumentgedFileContentIdDocumentGet(
                    {
                        idDocument: reglementDoc.idDocument,
                    },
                    { responseType: 'blob' },
                );

                setDocumentPreview({
                    content: docGedContent.data as unknown as Blob,
                    fileName: reglementDoc.filename || 'règlement',
                    fileType:
                        (getFileExtension(reglementDoc.filename as string) as DocumentExtension) ||
                        ('pdf' as DocumentExtension),
                });
            } catch (err) {
                console.log(err);
                enqueueSnackbar('Une erreur est survenue lors de la récupération du document', {
                    variant: 'error',
                });
            } finally {
                setIsSubmitting(false);
                setPreviewLoading(false);
            }
        }
    };

    const handleDownload = async (e: React.MouseEvent, reglementDoc: DocumentGed) => {
        if (reglementDoc.idDocument) {
            setIsSubmitting(true);
            try {
                const docGedContent = await documentGedApi.apiDocumentgedFileContentIdDocumentGet(
                    {
                        idDocument: reglementDoc.idDocument,
                    },
                    { responseType: 'blob' },
                );

                download(reglementDoc.filename as string, docGedContent.data as unknown as Blob);
            } catch (err) {
                console.log(err);
                enqueueSnackbar('Une erreur est survenue lors de la récupération du document', {
                    variant: 'error',
                });
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleDeletion = async (e: React.MouseEvent, reglementDoc: DocumentGed) => {
        if (reglementDoc.idDocument) {
            setIsSubmitting(true);
            try {
                const docGedContent = await documentGedApi.apiDocumentgedFileIdDocumentDelete({
                    idDocument: reglementDoc.idDocument,
                });
                if ([204].includes(docGedContent.status)) {
                    setFilteredDocGed((prevState) =>
                        prevState.filter((doc) => doc.idDocument !== reglementDoc.idDocument),
                    );

                    setReglementsDocs((prevState) =>
                        prevState.filter((doc) => doc.idDocument !== reglementDoc.idDocument),
                    );
                    enqueueSnackbar('Le fichier a bien été supprimé');
                }
            } catch (err) {
                console.log(err);
                enqueueSnackbar('Une erreur est survenue lors de la récupération du document', {
                    variant: 'error',
                });
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleOnSuccess = (createdFiles: DocumentGed[]) => {
        setFilteredDocGed((prevState) => [...prevState, ...createdFiles]);
        setReglementsDocs((prevState) => [...prevState, ...createdFiles]);
        enqueueSnackbar('Les fichiers ont bien été ajoutés');
        setIsDisplayedForm(false);
    };

    const handleDisplayAddDocGedForm = () => {
        setIsDisplayedForm((prevState) => !prevState);
    };

    return (
        <Box
            sx={{
                minHeight: '500px',
                maxHeight: '80vh',
                overflow: 'auto',
            }}
        >
            <CardSection
                sx={{
                    boxShadow: 'none',
                    marginTop: '-30px',
                }}
                header={
                    <AttachmentDocumentsHeading
                        titleDetails={reglementId ? `Règlement ${reglementId}` : 'Documents'}
                        totalCount={filteredDocGed.length}
                        additionalAction={
                            <HeaderActionButton
                                label={
                                    isDisplayedForm
                                        ? 'Afficher les documents'
                                        : 'Ajouter un document'
                                }
                                icon={isDisplayedForm ? arrowLeftIcon : plusIcon}
                                onClick={handleDisplayAddDocGedForm}
                            />
                        }
                    />
                }
                sxContent={{
                    padding: '0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px 0',
                }}
            >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around' }}>
                    {isDisplayedForm && reglementId ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flex: 1,
                                minWidth: '300px',
                                flexDirection: 'column',
                                gap: '8px',
                                paddingX: '8px',
                            }}
                        >
                            <UploaderProvider>
                                <DossierDocumentGedCreateForm
                                    reglementId={reglementId}
                                    dossier={dossier}
                                    onSuccess={handleOnSuccess}
                                    onClose={() => {}}
                                />
                            </UploaderProvider>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                flex: 1,
                                minWidth: '300px',
                                flexDirection: 'column',
                                gap: '8px',
                                paddingX: '8px',
                            }}
                        >
                            {/* ${dateToISOYmd(new Date(reglementDoc.timestamp as string))} */}
                            {filteredDocGed.map((reglementDoc) => (
                                <DocumentItem
                                    label={`Règlement ${reglementDoc.idReglement} `}
                                    uploadable={false}
                                    uploading={false}
                                    itemActions={{
                                        showTooltip: 'Afficher le document',
                                        downloadTooltip: 'Télécharger le document',
                                        deleteTooltip: 'Supprimer le fichier',
                                        confirmation: true,
                                        disabled: isSubmitting,
                                        onShow: (e: React.MouseEvent) =>
                                            handleShow(e, reglementDoc),
                                        onDownload: (e: React.MouseEvent) =>
                                            handleDownload(e, reglementDoc),
                                        onDelete: (e: React.MouseEvent) =>
                                            handleDeletion(e, reglementDoc),
                                    }}
                                    fileName={reglementDoc.filename}
                                    labelSx={{ minWidth: '200px' }}
                                />
                            ))}
                        </Box>
                    )}
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: '300px',
                            paddingX: '8px',
                            borderRadius: '8px',
                            border: `1px solid ${palette.grey[300]}}`,
                        }}
                    >
                        <Box sx={{ height: '50vh', overflowY: 'auto' }}>
                            <DocumentViewer isResponsive />
                        </Box>
                    </Box>
                </Box>
            </CardSection>
        </Box>
    );
};

export default DocumentsModalContent;
